import { TextInput, Textarea, Select, MultiSelect, NumberInput, Box, Text, Input, Checkbox } from '@mantine/core';
import { IconAlertTriangle } from '@nw4/ui/icons';
import { Controller } from 'react-hook-form';
import { SocialInput, PhoneInput, CountrySelect, ConversionInput, DateInput, GenderSelect } from '@nw4/ui';

/**
 * FieldRenderer - Renders form fields in vertical layout mode (label above field)
 */
export const FieldRenderer = ({
  field,
  register,
  control,
  errors,
  watchedValue,
  setValue,
  countryCode,
  conversions,
  segmentedVariant,
  socialVariant,
  phoneVariant,
}) => {
  const error = errors[field.name]?.message;
  const currentLength = typeof watchedValue === 'string' ? watchedValue.length : 0;
  const maxLength = field.validation?.maxLength;

  // Common props for Mantine inputs in vertical layout
  const commonProps = {
    label: null, // Handled by Input.Wrapper
    description: null,
    placeholder: field.placeholder || '',
    error: !!error, // Boolean to trigger red styling, message shown by Input.Wrapper
    required: field.required,
    withAsterisk: false,
  };


  // Error icon for validation feedback
  const errorIcon = error ? (
    <IconAlertTriangle size={18} stroke={1.5} className="input-error-icon" />
  ) : null;

  // Wrapper function for vertical layout
  const wrapField = (content, options = {}) => {
    const { showCharCount = false } = options;

    return (
      <Input.Wrapper
        label={field.label}
        description={field.description}
        error={error}
        required={field.required}
        withAsterisk={field.required}
      >
        {content}
        {showCharCount && maxLength && (
          <Text
            size="xs"
            c={currentLength > maxLength ? 'red' : 'dimmed'}
            ta="right"
            mt={4}
          >
            {currentLength} / {maxLength}
          </Text>
        )}
      </Input.Wrapper>
    );
  };

  const type = (field.type || '').toLowerCase();
  const normalizeMultiSelectValue = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };
  switch (type) {
    case 'bigtext':
      return wrapField(
        <Textarea
          {...commonProps}
          {...register(field.name)}
          minRows={3}
          autosize
          rightSection={errorIcon}
        />,
        { showCharCount: true }
      );

    case 'number':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <NumberInput
              {...commonProps}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              min={field.validation?.min}
              max={field.validation?.max}
              rightSection={errorIcon}
            />
          )}
        />
      );

    case 'choice':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <Select
              {...commonProps}
              data={field.options || []}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              //searchable
              clearable
              rightSection={errorIcon}
            />
          )}
        />
      );

    case 'multiplechoice':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <MultiSelect
              {...commonProps}
              data={field.options || []}
              value={normalizeMultiSelectValue(value)}
              onChange={onChange}
              onBlur={onBlur}
              searchable
              clearable
              rightSection={errorIcon}
            />
          )}
        />
      );

    case 'conversion':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <ConversionInput
              label={null}
              showLabel={false}
              error={!!error}
              required={field.required}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              conversion={field.conversion}
              featureName={field.featureName}
              description={null}
              additionalProps={field.additionalProps}
              metricsPosition={conversions.metricsPosition}
              metricsVariant={conversions.metricsVariant}
            />
          )}
        />
      );

    case 'email':
      return wrapField(
        <TextInput
          {...commonProps}
          type="email"
          {...register(field.name)}
          rightSection={errorIcon}
        />
      );

    case 'date':
    case 'birthdate':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onBlur } }) => {
            /*
            Mantine DateInput (@mantine/dates) — previous implementation:
            <DateInput
              {...commonProps}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              valueFormat="DD/MM/YYYY"
              rightSection={errorIcon}
            />
            */
            return (
              <DateInput
                {...commonProps}
                value={value}
                defaultLevel={type === 'birthdate' ? 'decade' : 'month'}
                onChange={(next) => {
                  setValue(field.name, next, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                onBlur={onBlur}
              />
            );
          }}
        />
      );

    case 'phone':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <PhoneInput
              label={null}
              showLabel={false}
              error={!!error}
              required={field.required}
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={field.placeholder}
              countryCode={countryCode}
              renderAs={phoneVariant}
            />
          )}
        />
      );

    case 'social':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <SocialInput
              label={null}
              showLabel={false}
              error={!!error}
              required={field.required}
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={field.placeholder}
              socialCode={field.socialCode}
              renderAs={socialVariant}
            />
          )}
        />
      );

    case 'country':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <CountrySelect
              label={null}
              error={!!error}
              required={field.required}
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={field.placeholder}
            />
          )}
        />
      );

    case 'boolean':
      return (
        <Input.Wrapper
          label={null}
          description={null}
          error={error}
          required={field.required}
          withAsterisk={field.required}
        >
          <Box display="flex" style={{ alignItems: 'flex-start', gap: 12 }}>
            <Controller
              name={field.name}
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <Checkbox
                  checked={value === true}
                  onChange={(event) => onChange(event.currentTarget.checked)}
                  onBlur={onBlur}
                  error={!!error}
                  label={null}
                  mt={2}
                />
              )}
            />
            <Box>
              {field.label && (
                <Text size="sm" fw={500} component="span">
                  {field.label}
                  {field.required && ' *'}
                </Text>
              )}
              {field.description && (
                <Text size="xs" c="dimmed" mt={4}>
                  {field.description}
                </Text>
              )}
            </Box>
          </Box>
        </Input.Wrapper>
      );

    case 'gender':
      return wrapField(
        <Controller
          name={field.name}
          control={control}
          defaultValue="Female"
          render={({ field: { value, onChange, onBlur } }) => (
            <GenderSelect
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error}
              renderAs={segmentedVariant}
            />
          )}
        />
      );

    case 'text':
    default:
      return wrapField(
        <TextInput
          {...commonProps}
          {...register(field.name)}
          rightSection={errorIcon}
        />
      );
  }
};
